/**
 * AlertBanner — Bandeau de notification standardisé.
 * 4 tons : success (vert), error (rouge), warning (jaune), info (bleu).
 * Fermable si onClose fourni. Icône automatique par type.
 */
import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertBannerProps {
  type: AlertType;
  message: React.ReactNode;
  onClose?: () => void;
  className?: string;
  /** Contenu additionnel à droite (bouton d'action, lien "voir plus"…) */
  action?: React.ReactNode;
}

const STYLES: Record<AlertType, string> = {
  success: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
  error: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
  warning: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  info: 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
};

const ICONS: Record<AlertType, React.FC<{ size: number }>> = {
  success: CheckCircle as any,
  error: XCircle as any,
  warning: AlertTriangle as any,
  info: Info as any,
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  type, message, onClose, action, className = '',
}) => {
  const Icon = ICONS[type];
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-lg text-sm border ${STYLES[type]} ${className}`}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Icon size={16} />
        <div className="min-w-0 flex-1">{message}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {action}
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:opacity-70 rounded"
            aria-label="Fermer"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertBanner;
